import type { MantineColorsTuple } from '@mantine/core';
import { colorsTuple as mantineColorsTuple, virtualColor as mantineVirtualColor } from '@mantine/core';
import { range } from 'radash';

export type BaseColor =
  | 'dark'
  | 'gray'
  | 'red'
  | 'pink'
  | 'grape'
  | 'violet'
  | 'indigo'
  | 'blue'
  | 'cyan'
  | 'green'
  | 'lime'
  | 'yellow'
  | 'orange'
  | 'teal';

export type SpecialColor = 'black' | 'white' | 'dimmed';

export type CustomColor = 'mouse-over' | 'paper' | 'selection' | 'primary';

export type IntrinsicColor = 'inherit' | 'initial' | 'unset' | 'currentcolor' | 'transparent';

export type Color = BaseColor | SpecialColor | CustomColor | IntrinsicColor;

export type ExtendedColor =
  | 'black'
  | 'white'
  | Color
  | `${BaseColor | SpecialColor | CustomColor}.${Shade}`
  | `${CustomColor}-${'light' | 'dark'}`;

type Shade = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export function colorsTuple(c0_9: string): MantineColorsTuple;
export function colorsTuple(c0_4: string, c5_9: string): MantineColorsTuple;
export function colorsTuple(c0_3: string, c4_5: string, c6_9: string): MantineColorsTuple;
export function colorsTuple(...colors: [string] | [string, string] | [string, string, string]): MantineColorsTuple {
  if (colors.length === 1) {
    return mantineColorsTuple(colors[0]);
  }

  if (colors.length === 2) {
    return mantineColorsTuple([...range(0, 4, colors[0]), ...range(5, 9, colors[1])]);
  }

  return mantineColorsTuple([...range(0, 3, colors[0]), ...range(4, 6, colors[1]), ...range(7, 9, colors[2])]);
}

export function virtualColor(
  name: CustomColor,
  light: ExtendedColor | MantineColorsTuple,
  dark: ExtendedColor | MantineColorsTuple,
): Record<string, MantineColorsTuple> {
  const colors: Record<string, MantineColorsTuple> = {};

  if (typeof light !== 'string') {
    colors[`${name}-light`] = light;
    light = `${name}-light`;
  }

  if (typeof dark !== 'string') {
    colors[`${name}-dark`] = dark;
    dark = `${name}-dark`;
  }

  colors[name] = mantineVirtualColor({ name, light, dark });

  return colors;
}
